import { Box, Typography } from '@material-ui/core';
import { isValid } from 'clients';
import clsx from 'clsx';
import FluctuatedPriceIcon from 'components-v2/atoms/FluctuatedPriceIcon';
import { InputProduct, LinkComp, MinusButton, PlusButton } from 'components/atoms';
import TagComponent from 'components/mocules/TagComponent';
import { FLUCTUATED_TYPE } from 'constants/Enums';
import { CART_QUANTITY_ICON } from 'constants/Icons';
import { GIFT_IMAGE, MISSING_IMAGE } from 'constants/Images';
import { useCart } from 'context';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { formatCurrency } from 'utils/FormatNumber';
import { ImageFallbackProductImage } from 'utils/ImageFallback';
import styles from './styles.module.css';

// product là cart item đã enrich, kèm price (giá mới) / currentPrice (giá lúc thêm vào
// giỏ) / fluctuatedType / warningMessage lấy từ fluctuatedItems của BE.
// isQuantityReadOnly: ở /checkout chỉ cho xem số lượng, muốn sửa thì quay lại giỏ hàng.
const CartPriceFluctuationItem = ({ product, isMobile = false, isQuantityReadOnly = false }) => {
  const { t: translateCart } = useTranslation('cart');
  const { updateCartItem, validate200Item } = useCart();

  const {
    sku,
    name = '',
    slug = '',
    volume = '',
    tags = [],
    statusData = {},
    expiredDate = '',
    lotDates = [],
    isDeal = false,
    deal = null,
    quantity = 0,
    price = 0,
    currentPrice = 0,
    fluctuatedType,
    warningMessage = '',
    imagesProxy = [],
    isGift = false,
    maxQuantity = 0,
    messageLimitOrder = '',
  } = product || {};

  const [value, setValue] = useState(quantity);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setValue(quantity);
  }, [quantity]);

  const isIncreased = fluctuatedType === FLUCTUATED_TYPE.INCREASED;
  const remainingPrice = Math.abs(price - currentPrice);

  // ngày cận date nằm ở lô có isNearExpired, expiredDate của cart item thường rỗng
  const nearExpiredLot = lotDates?.find((item) => item?.isNearExpired === true);

  const handleUpdateQuantity = async (newQuantity) => {
    if (newQuantity < 1) return;
    setValue(newQuantity);
    if (newQuantity === quantity) return;

    setIsUpdating(true);
    const response = await updateCartItem({ product, q: newQuantity }, true, true);
    setIsUpdating(false);
    // BE từ chối (vượt giới hạn mua...) thì trả input về số lượng thật trong giỏ
    if (!isValid(response)) setValue(quantity);
  };

  const handleDecrease = () => handleUpdateQuantity(value - 1);

  const handleIncrease = () => {
    if (!validate200Item(sku)) return;
    handleUpdateQuantity(value + 1);
  };

  const handleInputChange = (e) => {
    const val = e?.currentTarget?.value?.replace(/[^\d]/g, '');
    setValue(val === '' ? '' : Number(val));
  };

  const handleBlur = () => {
    const newQuantity = Number(value) || 0;
    if (newQuantity < 1) {
      setValue(quantity);
      return;
    }
    handleUpdateQuantity(maxQuantity > 0 ? Math.min(newQuantity, maxQuantity) : newQuantity);
  };

  return (
    <Box className={clsx(styles.item, isMobile && styles.item_mobile)}>
      <Box className={styles.image_wrapper}>
        <ImageFallbackProductImage
          src={imagesProxy?.[0] || (isGift ? GIFT_IMAGE : MISSING_IMAGE)}
          fallbackSrc={isGift ? GIFT_IMAGE : MISSING_IMAGE}
          width={isMobile ? 96 : 72}
          height={isMobile ? 96 : 72}
          quality={100}
          alt={`product-img-${sku}`}
          className={styles.image}
        />
      </Box>

      <Box className={styles.content}>
        <LinkComp href={`/product/${slug || ''}/loading`} prefetch={false} className={styles.link}>
          <Typography className={styles.product_name}>
            {isDeal && deal ? deal?.name : name}
          </Typography>
        </LinkComp>
        {!!volume && <Typography className={styles.product_volume}>{volume}</Typography>}
        <Box className={styles.product_tags}>
          <TagComponent
            product={{
              tags,
              statusData,
              expiredDate: nearExpiredLot?.expiredDate || expiredDate || '',
            }}
            isGift={isGift}
          />
        </Box>
      </Box>

      <Box className={styles.price_block}>
        <Box className={styles.price_row}>
          <span className={styles.price_label}>{translateCart('original_price')}</span>
          <span className={styles.old_price}>{formatCurrency(currentPrice)}</span>
        </Box>
        <Box className={styles.price_row}>
          <span className={clsx(styles.price_label, styles.price_label_strong)}>
            {translateCart('new_price')}
          </span>
          <span className={styles.new_price}>{formatCurrency(price)}</span>
        </Box>
        <Box className={styles.fluctuation_row}>
          <FluctuatedPriceIcon fluctuatedType={fluctuatedType} />
          <span className={clsx(styles.fluctuation_text, isIncreased && styles.increased)}>
            {`${isIncreased ? translateCart('price_increase') : translateCart('price_save')} ${formatCurrency(remainingPrice)}`}
          </span>
        </Box>
        {!!warningMessage && (
          <Box className={styles.price_row}>
            <span className={styles.price_label}>{translateCart('price_change_reason')}</span>
            <span className={styles.reason_text}>{warningMessage}</span>
          </Box>
        )}
      </Box>

      <Box className={styles.quantity_block}>
        {!!messageLimitOrder && <span className={styles.limit_text}>{messageLimitOrder}</span>}
        <Box className={styles.quantity_control}>
          {!isQuantityReadOnly && (
            <MinusButton
              className={styles.minus_button}
              disabled={isUpdating || value <= 1}
              onClick={handleDecrease}
            />
          )}
          <Box className={styles.quantity_input_wrapper}>
            {isMobile && (
              <Image
                src={CART_QUANTITY_ICON}
                width={14}
                height={13}
                layout="fixed"
                alt="quantity"
              />
            )}
            <InputProduct
              className={styles.quantity_input}
              product={product}
              value={value}
              onChange={handleInputChange}
              onBlur={handleBlur}
              disabled={isUpdating || isQuantityReadOnly}
              style={isMobile ? { width: `${String(value ?? '').length + 1}ch` } : undefined}
            />
          </Box>
          {!isQuantityReadOnly && (
            <PlusButton
              disabled={isUpdating || (maxQuantity > 0 && value >= maxQuantity)}
              onClick={handleIncrease}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CartPriceFluctuationItem;
